(ns frontend.ux-overhaul.listing.preview.event
  "The events of the record preview of a listing. The state is at
   `frontend.ux-overhaul.listing.generic.model/preview-path`: a map with `:entity-id`, `:entity-type`,
   `:status` (`:loading`, `:loaded` or `:failed`), `:card-config` (the compiled card config),
   `:pinned-fields` (the fields that the preview shows also when they are empty), `:request-body`
   (the body of the search that loads the record), `:request-id` (the id of the request that the
   preview waits for), `:open-token` (the token of the current opening, for the save guard),
   `:reloading?` (true while a reload of the record entity is in progress),
   `:card` (the document of the record) and `:error`. The preview is open
   when the state has an `:entity-id`. The response of an earlier request is ignored, also when
   the earlier request was for the same record. So a fast click on a second record does not show
   the first one, and a reopen of a record does not take the late failure of the first open.
   `::reload` loads the open record again after a change of the record, for example a status
   change. It makes a new request id, so the same guard holds."
  (:require
   [frontend.ux-overhaul.listing.generic.model :as model]
   [frontend.ux-overhaul.listing.preview.model :as preview-model]
   [frontend.updates.entity-scope :as entity-scope]
   [fx.ajax :as ajax]
   [re-frame.core :as rf]
   [ui.fx.toast :as toast]
   [ui.view.list-opensearch.event :as list-event]))

(defn- send-fx
  "The effect that loads the record. The two callbacks carry `request-id`, and a response is
   accepted only when it matches the request id in the state."
  [{:keys [listing-id entity-id request-id body]}]
  {:operation  :search/search
   :body       body
   :timeout-ms 60000
   :on-ok      [::loaded {:listing-id listing-id
                          :entity-id  entity-id
                          :request-id request-id}]
   :on-error   [::failed {:listing-id listing-id
                          :entity-id  entity-id
                          :request-id request-id}]})

(defn open-fx
  "Open the preview of a record and load it. `:card-config` is the card config of
   `frontend.ux-overhaul.listing.preview.model/card-config`. The view builds it, because it reads
   the schema of the tenant, and an event must not read the session. `:request-id` is a new
   value for each open, for example a uuid. The view makes it for the same reason. The two
   callbacks of the request carry it, and a response is accepted only when it matches.

   A pipeline entity (a deal and the other opportunity types) also loads the record
   from the backend, so that the preview can edit the fields of the stage that the
   record is in. The load is a separate request from the card search. See
   `::load-entity`. A non-pipeline entity keeps the read-only preview and loads no
   entity."
  [{:keys [db]} [_ {:keys [listing-id entity-id entity-type pipeline-id card-config request-id]}]]
  (let [body      (preview-model/request-body {:entity-type entity-type
                                               :entity-id   entity-id
                                               :pipeline-id pipeline-id
                                               :card-config card-config})
        editable? (preview-model/pipeline-entity-type? entity-type)
        state     (cond-> {:entity-id     entity-id
                           :entity-type   entity-type
                           :request-body  body
                           :request-id    request-id
                           :open-token    request-id
                           :status        :loading
                           :card-config   (list-event/compile-card-config card-config)
                           :pinned-fields (:pinned-fields card-config)}
                    editable? (assoc :entity-status     :loading
                                     :entity-request-id request-id))]
    (cond-> {:db         (assoc-in db (model/preview-path listing-id) state)
             ::ajax/send (send-fx {:listing-id listing-id
                                   :entity-id  entity-id
                                   :request-id request-id
                                   :body       body})}
      editable? (assoc ::load-entity {:listing-id listing-id
                                      :entity-id  entity-id
                                      :request-id request-id}))))

(defn reload-fx
  "Load the record of the open preview again, for example after a status change that the
   panel started. The state keeps the card until the new card arrives, so the panel does not
   flash, and it gets a new request id, so a response of an earlier request is ignored. Nothing
   happens when the preview is closed. The new id comes from the coeffect `::request-id`.

   An editable preview also loads the record entity again, so that the editable
   fields show the new values. The reload keeps the current edits of the user,
   because a background reload must not drop work in progress. It sets `:reloading?`,
   so the fields are read-only and Save is off until the entity arrives. This stops
   an edit or a save against the values from before the reload. The new values of
   fields that the user did not edit arrive from the entity."
  [{:keys [db request-id]} [_ listing-id]]
  (let [path (model/preview-path listing-id)
        {:keys [entity-id request-body entity-status]} (get-in db path)
        editable? (some? entity-status)]
    (if (and entity-id request-body)
      (let [new-db (cond-> (assoc-in db (conj path :request-id) request-id)
                     editable? (-> (assoc-in (conj path :entity-request-id) request-id)
                                   (assoc-in (conj path :reloading?) true)))]
        (cond-> {:db         new-db
                 ::ajax/send (send-fx {:listing-id listing-id
                                       :entity-id  entity-id
                                       :request-id request-id
                                       :body       request-body})}
          editable? (assoc ::load-entity {:listing-id listing-id
                                          :entity-id  entity-id
                                          :request-id request-id})))
      {})))

(defn- current?
  "True when the preview of the listing `listing-id` waits for the request `request-id`. A
   request with no id is never current."
  [{:keys [db listing-id request-id]}]
  (and (some? request-id)
       (= request-id (get-in db (conj (model/preview-path listing-id) :request-id)))))

(defn loaded-fx
  "A record is loaded. The state gets the card, or the status `:failed` when the response has no
   hit for the record, for example when the user cannot read it any more."
  [{:keys [db]} [_ {:keys [listing-id entity-id request-id]} resp]]
  (if (current? {:db         db
                 :listing-id listing-id
                 :request-id request-id})
    (let [card (preview-model/response->card {:entity-id entity-id
                                              :resp      resp})
          path (model/preview-path listing-id)]
      {:db (if card
             (update-in db path assoc :status :loaded :card card :error nil)
             (update-in db path assoc :status :failed :card nil :error {:cause :not-found}))})
    {:db db}))

(defn failed-fx
  "The request failed. A panel that already shows a card keeps it, because the user can still
   read the record, but the state gets the error `:reload-failed`: the panel says that the record
   may be out of date, and it does not offer the actions of the old record. The next load that
   works clears the error. A panel with no card gets the status `:failed`."
  [{:keys [db]} [_ {:keys [listing-id request-id]} err]]
  (if (current? {:db         db
                 :listing-id listing-id
                 :request-id request-id})
    (let [path (model/preview-path listing-id)
          error {:cause :request-failed
                 :error err}]
      {:db (if (:card (get-in db path))
             (update-in db path assoc :error (assoc error :cause :reload-failed))
             (update-in db path assoc :status :failed :error error))})
    {:db db}))

(defn close-fx
  "Close the preview. The state is cleared, so a response that arrives later is ignored."
  [{:keys [db]} [_ listing-id]]
  {:db (assoc-in db (model/preview-path listing-id) nil)})

(defn- entity-request-current?
  "True when the preview of `listing-id` waits for the entity load `request-id`
   of the record `entity-id`. A load for another record or an earlier load is not
   current, so a late answer is ignored."
  [{:keys [db listing-id entity-id request-id]}]
  (let [state (get-in db (model/preview-path listing-id))]
    (and (some? request-id)
         (= entity-id (:entity-id state))
         (= request-id (:entity-request-id state)))))

(defn entity-loaded-fx
  "The record entity is loaded. The state gets the entity and the status `:loaded`,
   it clears a reload error, and it stops the reload state, so the editable fields
   show its values and become editable again. The answer of an earlier load is
   ignored."
  [{:keys [db]} [_ {:keys [listing-id entity-id request-id]} entity]]
  (if (entity-request-current? {:db         db
                                :listing-id listing-id
                                :entity-id  entity-id
                                :request-id request-id})
    {:db (update-in db (model/preview-path listing-id)
                    assoc :entity entity :entity-status :loaded
                    :entity-error nil :reloading? false)}
    {:db db}))

(defn entity-load-failed-fx
  "The load of the record entity failed. A first load that fails has no entity to
   show, so the status is `:failed` and the preview shows the read-only card with an
   alert. A reload that fails keeps the entity that the panel already shows, but it
   records `:entity-error`, so the preview marks the fields out of date and blocks
   editing until a reload works. This stops a save of stale values and does not drop
   the edits of the user. Either way the reload state stops. The answer of an earlier
   load is ignored."
  [{:keys [db]} [_ {:keys [listing-id entity-id request-id]} _error]]
  (if (entity-request-current? {:db         db
                                :listing-id listing-id
                                :entity-id  entity-id
                                :request-id request-id})
    (let [path (model/preview-path listing-id)]
      (if (:entity (get-in db path))
        {:db (-> db
                 (assoc-in (conj path :entity-error) {:cause :reload-failed})
                 (assoc-in (conj path :reloading?) false))}
        {:db (-> db
                 (assoc-in (conj path :entity-status) :failed)
                 (assoc-in (conj path :reloading?) false))}))
    {:db db}))

(defn save-start-fx
  "The save of the edited fields started. The panel shows that a save is in
   progress and does not start a second save."
  [{:keys [db]} [_ listing-id]]
  {:db (assoc-in db (conj (model/preview-path listing-id) :saving?) true)})

(defn- save-current?
  "True when the preview of `listing-id` still shows the record and the opening that
   started the save. `open-token` is the token of the opening at save time. A close
   and a reopen of the same record make a new token, so a late save answer of the
   earlier opening does not change the new opening."
  [{:keys [db listing-id entity-id open-token]}]
  (let [state (get-in db (model/preview-path listing-id))]
    (and (= entity-id (:entity-id state))
         (= open-token (:open-token state)))))

(defn save-ok-fx
  "The save of the edited fields was accepted. The state keeps the saved values on
   the entity at once, so the panel does not show the old values while it waits for
   the reload. The view clears the edits of the editor in place after the save, so
   the state does not remount the editor. The entity is loaded again from the
   backend for the values that the backend may change. It sets `:reloading?`, so the
   fields stay read-only and Save stays off from the save until the reload completes.
   The grid and the Kanban refresh on the completion of the search index, see
   `frontend.ux-overhaul.listing.pipeline-entity`. A save answer of an opening that
   the user has closed and reopened is ignored, so it does not drop the new edits."
  [{:keys [db request-id]} [_ {:keys [listing-id entity-id open-token saved]}]]
  (let [path (model/preview-path listing-id)]
    (if (save-current? {:db         db
                        :listing-id listing-id
                        :entity-id  entity-id
                        :open-token open-token})
      {:db          (update-in db path
                               (fn [s]
                                 (-> s
                                     (assoc :saving? false)
                                     (update :entity merge saved)
                                     (assoc :entity-request-id request-id)
                                     (assoc :reloading? true))))
       ::load-entity {:listing-id listing-id
                      :entity-id  entity-id
                      :request-id request-id}}
      {:db db})))

(defn save-error-fx
  "The save of the edited fields failed. The panel stops the save state and shows a
   message. The edits of the user stay, so the user can try again. A save answer of
   an opening that the user has closed and reopened does not change the new opening.
   `:message` is built in the view, so that the handler does not read the session."
  [{:keys [db]} [_ {:keys [listing-id entity-id open-token message]}]]
  (cond-> {:db (if (save-current? {:db         db
                                   :listing-id listing-id
                                   :entity-id  entity-id
                                   :open-token open-token})
                 (assoc-in db (conj (model/preview-path listing-id) :saving?) false)
                 db)}
    message (assoc ::toast/error message)))

(rf/reg-fx
 ::load-entity
 (fn [{:keys [listing-id entity-id request-id]}]
   (entity-scope/load-entity!
    {:entity-id entity-id
     :on-loaded (fn [entity _entities]
                  (rf/dispatch [::entity-loaded {:listing-id listing-id
                                                 :entity-id  entity-id
                                                 :request-id request-id}
                                entity]))
     :on-error  (fn [error]
                  (rf/dispatch [::entity-load-failed {:listing-id listing-id
                                                      :entity-id  entity-id
                                                      :request-id request-id}
                                error]))})))

(rf/reg-cofx
 ::request-id
 (fn [coeffects]
   (assoc coeffects :request-id (random-uuid))))

(rf/reg-event-fx ::open open-fx)
(rf/reg-event-fx ::reload [(rf/inject-cofx ::request-id)] reload-fx)
(rf/reg-event-fx ::loaded loaded-fx)
(rf/reg-event-fx ::failed failed-fx)
(rf/reg-event-fx ::close close-fx)
(rf/reg-event-fx ::entity-loaded entity-loaded-fx)
(rf/reg-event-fx ::entity-load-failed entity-load-failed-fx)
(rf/reg-event-fx ::save-start save-start-fx)
(rf/reg-event-fx ::save-ok [(rf/inject-cofx ::request-id)] save-ok-fx)
(rf/reg-event-fx ::save-error save-error-fx)
